<div class="compare-container">

  @if (loading) {
  <div class="state-center">
    <fa-icon [icon]="icons.faSpinner" animation="spin"></fa-icon>
    <p>{{ 'MESSAGE.LOADING' | translate }}</p>
  </div>
  }

  @if (error && !loading) {
  <div class="state-center error">
    <fa-icon [icon]="icons.faExclamationTriangle"></fa-icon>
    <p>{{ error | translate }}</p>
  </div>
  }

  @if (!loading && !error && dataList.length >= 2) {
  <div class="compare-content">

    <div class="page-header">
      <button class="back-button" (click)="goBack()" [title]="'BUTTON.BACK' | translate">
        <fa-icon [icon]="icons.faArrowLeft"></fa-icon>
      </button>
      <div class="header-title">
        <h1>{{ 'COMPARE.TITLE' | translate }}</h1>
        <span class="header-subtitle">
          {{ dataList.length }} {{ 'COMPARE.RESULTS_SELECTED' | translate }}
        </span>
      </div>
    </div>

    <div class="config-strip">
      @for (d of dataList; track $index) {
      <div class="config-card" [style.border-left-color]="colorFor($index).border" [class.reference]="resultIds[$index] === referenceResultId">
        <div class="config-card-header">
          <span class="config-result-label" [style.color]="colorFor($index).border">{{ labelFor($index) }}</span>
          @if (resultIds[$index] === referenceResultId) {
          <span class="reference-badge">
            <fa-icon [icon]="icons.faCheckCircle"></fa-icon>
            {{ 'COMPARE.REFERENCE' | translate }}
          </span>
          } @else {
          <button class="set-reference-btn" (click)="setReferenceResult(resultIds[$index])" [title]="'COMPARE.SET_AS_REFERENCE' | translate">
            <fa-icon [icon]="icons.faGaugeHigh"></fa-icon>
            {{ 'COMPARE.SET_AS_REFERENCE' | translate }}
          </button>
          }
        </div>
        @if (d.config_labels.llm) {
        <span class="config-badge llm">{{ 'INTERACTIONS.CONFIG.DELFOS_LLM' | translate }}: {{ d.config_labels.llm }}</span>
        }
        @if (d.config_labels.ocr) {
        <span class="config-badge ocr">{{ 'INTERACTIONS.CONFIG.DI2WINOCR_SERVER' | translate }}: {{ d.config_labels.ocr }}</span>
        }
        @if (d.config_labels.use_multimodal) {
        <span class="config-badge multimodal">{{ 'COMPARE.MULTIMODAL' | translate }}</span>
        }
      </div>
      }
    </div>

    <div class="tabs-nav">
      <button class="tab-button" [class.active]="activeTab === 'list'" (click)="setActiveTab('list')">
        <fa-icon [icon]="icons.faList"></fa-icon>
        {{ 'COMPARE.TABS.LIST' | translate }}
      </button>
      <button class="tab-button" [class.active]="activeTab === 'stats'" (click)="setActiveTab('stats')">
        <fa-icon [icon]="icons.faChartColumn"></fa-icon>
        {{ 'COMPARE.TABS.STATISTICS' | translate }}
      </button>
    </div>

    <div class="tab-panel" [class.hidden]="activeTab !== 'stats'">
      <div class="stats-summary">
        @for (d of dataList; track $index) {
        <div class="stats-summary-card" [style.border-left-color]="colorFor($index).border">
          <span class="stats-summary-label" [style.color]="colorFor($index).border">{{ labelFor($index) }}</span>
          <div class="stats-summary-metrics">
            <span class="metric-info">{{ 'COMPARE.METRICS.AVG_CONFIDENCE' | translate }}: {{ d.stats.avg_confidence !== null ? formatConfidence(d.stats.avg_confidence) : '—' }}</span>
            @if (!isClassificationMode) {
            <span class="metric-info">{{ 'COMPARE.METRICS.TOTAL_FIELDS' | translate }}: {{ d.stats.total_fields }}</span>
            }
          </div>
        </div>
        }
      </div>

      <div class="charts-section">
        <div class="charts-row">
          <div class="chart-card">
            <p class="chart-card-title">{{ 'COMPARE.CHARTS.TOKENS' | translate }}</p>
            <div class="chart-wrapper">
              <canvas #tokensChart></canvas>
            </div>
          </div>
          <div class="chart-card">
            <p class="chart-card-title">{{ 'COMPARE.CHARTS.METRICS' | translate }}</p>
            <div class="chart-wrapper">
              <canvas #metricsChart></canvas>
            </div>
          </div>
        </div>
        @if (isClassificationMode) {
        <div class="charts-row charts-row-single">
          <div class="chart-card">
            <p class="chart-card-title">{{ 'COMPARE.CHARTS.CLASS_AGREEMENT' | translate }}</p>
            <div class="chart-wrapper">
              <canvas #classAgreementChart></canvas>
            </div>
          </div>
        </div>
        } @else {
        <div class="charts-row">
          <div class="chart-card">
            <p class="chart-card-title">{{ 'COMPARE.CHARTS.ACCURACY_BY_FILE' | translate }}</p>
            <div class="chart-wrapper">
              <canvas #archiveAccuracyChart></canvas>
            </div>
          </div>
          <div class="chart-card">
            <p class="chart-card-title">{{ 'COMPARE.CHARTS.QUALITY_DISTRIBUTION' | translate }}</p>
            <div class="chart-wrapper">
              <canvas #qualityDistributionChart></canvas>
            </div>
          </div>
        </div>
        <div class="charts-row charts-row-single">
          <div class="chart-card">
            <p class="chart-card-title">{{ 'COMPARE.CHARTS.PROBLEMATIC_FIELDS' | translate }}</p>
            <div class="chart-wrapper chart-wrapper-tall">
              <canvas #problematicFieldsChart></canvas>
            </div>
          </div>
        </div>
        }
      </div>
    </div>

    @if (allArchives.length > 0) {
    <div class="kv-section tab-panel" [class.hidden]="activeTab !== 'list'">
      <div class="kv-header">
        <h3>{{ 'COMPARE.KV_TABLE.TITLE' | translate }}</h3>
        <div class="archive-nav">
          <span class="archive-count">{{ currentArchiveIndex + 1 }} / {{ allArchives.length }}</span>
          <button class="archive-nav-button" (click)="goToPrevArchive()" [disabled]="!hasPrevArchive"
            [title]="'COMMON.PAGINATION.PREVIOUS' | translate">
            <fa-icon [icon]="icons.faChevronLeft"></fa-icon>
          </button>
          <select class="archive-select" [(ngModel)]="selectedArchive" (change)="onArchiveChange(selectedArchive)">
            @for (archive of allArchives; track archive) {
            <option [value]="archive" [title]="archive">{{ archive | truncateMiddle:30 }}</option>
            }
          </select>
          <button class="archive-nav-button" (click)="goToNextArchive()" [disabled]="!hasNextArchive"
            [title]="'COMMON.PAGINATION.NEXT' | translate">
            <fa-icon [icon]="icons.faChevronRight"></fa-icon>
          </button>
        </div>
      </div>

      <div class="table-filters">
        <input type="text" class="filter-input" [placeholder]="'COMPARE.FILTERS.SEARCH' | translate" [(ngModel)]="fieldFilter"
          (input)="onFieldFilterChange()">
        <ng-select
          class="filter-multiselect"
          [items]="allKvKeys"
          [(ngModel)]="selectedFieldFilters"
          (ngModelChange)="onFieldSelectChange()"
          [multiple]="true"
          [closeOnSelect]="false"
          [placeholder]="'COMPARE.FILTERS.ALL_FIELDS' | translate"
          appendTo="body">
        </ng-select>
        <select class="filter-select" [(ngModel)]="minSimilarity" (change)="onSimilarityFilterChange()">
          <option value="">{{ 'COMPARE.FILTERS.ALL_ACCURACY' | translate }}</option>
          <option value="0.9">≤ 90%</option>
          <option value="0.7">≤ 70%</option>
          <option value="0.5">≤ 50%</option>
          <option value="0.2">≤ 20%</option>
        </select>
      </div>

      @if (resultsMissingSelectedArchive.length > 0) {
      <p class="archive-missing-hint">
        <fa-icon [icon]="icons.faExclamationTriangle"></fa-icon>
        {{ 'COMPARE.KV_TABLE.ARCHIVE_MISSING_IN' | translate }}
        @for (i of resultsMissingSelectedArchive; track i) {
        <span class="missing-result-tag" [style.border-color]="colorFor(i).border">{{ labelFor(i) }}</span>
        }
      </p>
      }

      <div class="kv-table-wrapper">
        <ag-grid-angular
          [class.ag-theme-quartz-dark]="isDarkTheme"
          [class.ag-theme-quartz]="!isDarkTheme"
          [rowData]="gridRowData"
          [columnDefs]="gridColDefs"
          [domLayout]="'normal'"
          [enableBrowserTooltips]="true"
          [defaultColDef]="{ sortable: true, filter: true }"
          [getRowClass]="getRowClass"
          [getRowId]="getRowId"
          (gridReady)="onGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>
    }

    @if (allArchives.length === 0) {
    <div class="state-center">
      <p>{{ 'COMPARE.NO_ARCHIVES' | translate }}</p>
    </div>
    }

  </div>
  }

</div>